/**
 * Advanced Search Config Adapter
 * Transforms Product Field Registry into Advanced Search field configurations
 */

import { FieldConfig } from '../../../components/TableSearch/types';
import { PRODUCT_FIELDS_REGISTRY } from '../fields';
import { BaseFieldDefinition } from '../../../shared/types/fieldRegistry';
import {
  getContextAwareConfigFromRegistry,
  toStandardAsyncSelectConfig,
} from '../../../shared/utils/contextAwareAsyncSelectConfig';

/**
 * Convert a field definition to Advanced Search field config
 * Handles flattening of relational fields for searching
 */
function toSearchFieldConfig(
  field: BaseFieldDefinition
): FieldConfig | FieldConfig[] {
  // Handle relational fields - use async select for API-based relations
  if (field.isRelation && field.relationConfig) {
    const { displayField, statusField } = field.relationConfig;

    const configs: FieldConfig[] = [];

    // Get context-aware config for search context
    // Search context uses searchField (name) for value (query matching)
    const contextConfig = getContextAwareConfigFromRegistry(
      PRODUCT_FIELDS_REGISTRY,
      field.key as string,
      'search'
    );

    if (contextConfig) {
      // Convert to standard config for component props
      const asyncConfig = toStandardAsyncSelectConfig(contextConfig);

      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'selection', // Still 'selection' but with async config
        apiEndpoint: asyncConfig.apiEndpoint,
        searchField: asyncConfig.searchField,
        valueField: asyncConfig.valueField, // Uses name field for query matching
        labelField: asyncConfig.labelField,
      });
    } else {
      // Fallback to string search if no async config
      configs.push({
        key: displayField,
        label: field.label,
        dataType: 'string',
      });
    }

    // If there's a status field, add it as a separate searchable field.
    // Label comes from the status field's own registry entry (e.g.
    // 'facility_status' -> 'Facility Status') rather than being hardcoded —
    // this relation branch is shared by every relation with a statusField
    // (manufacturers, facilities), not just manufacturers.
    if (statusField && field.selectionOptions) {
      const statusFieldDef = PRODUCT_FIELDS_REGISTRY.find(
        f => f.key === statusField
      );
      configs.push({
        key: statusField,
        label: statusFieldDef?.label ?? statusField,
        dataType: 'selection',
        options: field.selectionOptions,
      });
    }

    return configs;
  }

  // Handle array fields (synonyms, replaced_rns) - search as strings, unless
  // the field declares a fixed set of selectionOptions (e.g. dmf_type only
  // ever holds 'II'/'IV' — see MATCHABLE_DMF_TYPES) in which case it's a
  // selection control instead of free text.
  if (field.dataType === 'array') {
    // Map to singular form for search key
    const searchKey =
      field.key === 'synonyms'
        ? 'product_synonym'
        : field.key === 'replaced_rns'
          ? 'replaced_rn'
          : field.key;

    if (field.selectionOptions) {
      return {
        key: searchKey as string,
        label: field.label,
        dataType: 'selection',
        options: field.selectionOptions,
      };
    }

    return {
      key: searchKey as string,
      label: field.label,
      dataType: 'string',
    };
  }

  // Standard fields - map directly
  const dataType =
    field.dataType === 'object'
      ? 'string'
      : field.dataType === 'boolean'
        ? 'selection'
        : (field.dataType as 'string' | 'number' | 'date' | 'selection');

  return {
    key: field.key as string,
    label: field.label,
    dataType,
    options: field.selectionOptions,
    // Boolean fields (is_cas_verified, is_fda_linked, is_ndc_linked) are
    // BIT NOT NULL DEFAULT 0 — they can never be NULL, so isEmpty/isNotEmpty
    // would always return 0 results and must be excluded from the operator list.
    nullable: field.dataType === 'boolean' ? false : undefined,
  };
}

/**
 * Convert Product Field Registry to Advanced Search field configurations
 * @returns Array of FieldConfig for Advanced Search component
 */
export function toAdvancedSearchConfig(): FieldConfig[] {
  const searchFields = PRODUCT_FIELDS_REGISTRY.filter(f =>
    f.surfaces?.includes('search')
  );

  const configs: FieldConfig[] = [];

  searchFields.forEach(field => {
    const config = toSearchFieldConfig(field);
    if (Array.isArray(config)) {
      configs.push(...config);
    } else {
      configs.push(config);
    }
  });

  return configs;
}

/**
 * Get searchable field keys (useful for validation)
 */
export function getSearchableFieldKeys(): string[] {
  return toAdvancedSearchConfig().map(config => config.key);
}

/**
 * Convert Product Field Registry to Advanced Search field configurations for the
 * flat products API (`ProductsRowGroupingDataGrid`).
 *
 * Differences from `toAdvancedSearchConfig()`:
 * - Relational fields (manufacturer, facility, grade, therapeutic area) use entity
 *   **IDs** as the selected value (modal context) instead of names (search context).
 *   This matches the new ID columns added to `PRODUCTS_FLAT_FIELD_MAPPING` in
 *   `api/sql/productsFlat.js` (Task 7.1).
 * - The async-select components search by name but submit the integer ID to the API.
 */
export function toFlatAdvancedSearchConfig(): FieldConfig[] {
  // Start with all standard searchable field configs
  const standardConfigs = toAdvancedSearchConfig();

  // Remove name-based async-select configs for relational fields —
  // they are replaced with ID-based equivalents below.
  const relationalDisplayFields = new Set([
    'manufacturer_name',
    'facility_name',
    'grade_name',
    'therapeutic_area_name',
  ]);
  const nonRelationalConfigs = standardConfigs.filter(
    c => !relationalDisplayFields.has(c.key) || !c.apiEndpoint
  );

  // Build ID-based FieldConfig for each relational field using modal context
  // (modal context = valueField is the ID, not the display name).
  const buildIdConfig = (
    key: string,
    label: string,
    registryKey: string
  ): FieldConfig | null => {
    const ctx = getContextAwareConfigFromRegistry(
      PRODUCT_FIELDS_REGISTRY,
      registryKey,
      'modal'
    );
    if (!ctx) return null;
    const standard = toStandardAsyncSelectConfig(ctx);
    return {
      key,
      label,
      dataType: 'selection',
      apiEndpoint: standard.apiEndpoint,
      searchField: standard.searchField,
      valueField: standard.valueField,
      labelField: standard.labelField,
    };
  };

  const idConfigs: FieldConfig[] = [
    // Labeled "Manufacturer Company Name", not "Manufacturer" -- this IS
    // the manufacturer-selection field (see the manufacturer_name registry
    // entry's comment in fields.ts for why there isn't also a free-text
    // "Manufacturer Company Name" field competing with this dropdown).
    buildIdConfig(
      'manufacturer_id',
      'Manufacturer Company Name',
      'manufacturers'
    ),
    buildIdConfig('facility_id', 'Facility', 'facilities'),
    // grade_id: async select using grade_id as the submitted value. This is
    // the sole "Product Type" field in the flat Advanced Search panel.
    // Derived via buildIdConfig like its siblings (issue #585 Task 5.3) —
    // previously hand-written, not registry-driven.
    buildIdConfig('grade_id', 'Product Type', 'grade'),
    buildIdConfig(
      'therapeutic_area_id',
      'Therapeutic Area',
      'therapeutic_areas'
    ),
  ].filter((c): c is FieldConfig => {
    if (c !== null) return true;
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        'buildIdConfig: registry key not found — an Advanced Search relational field will be silently excluded'
      );
    }
    return false;
  });

  return sortNameFieldsFirst([...nonRelationalConfigs, ...idConfigs]);
}

// ---------------------------------------------------------------------------
// Field ordering helpers
// ---------------------------------------------------------------------------

/**
 * Enforced display order for the product name fields in the flat
 * Advanced Search panel (Sub-task 1.12). Keys not listed here retain their
 * registry order. product_cas_name removed (issue #585 Task 9 — the field
 * is fully retired, no longer in the registry at all). man_product_name
 * removed (issue #585 Task 5 follow-up) — it was a legacy alias for the
 * exact same Product_Names bucket column (pn_mfr.name) as the 'manufacturer'
 * field below, a second field with identical results under a near-identical
 * label ("Manufacturer Product Name" vs "...  (All Sources)"); the registry
 * entry itself was deleted, not just de-surfaced.
 */
const NAME_FIELD_ORDER: string[] = [
  'preferred_product_name', // Product Name
  'lgm_product_name', // LGM Product Name
  'ob_product_name', // Orange Book Name
];

/**
 * Sort a FieldConfig array so the five name fields appear in the specified order
 * (NAME_FIELD_ORDER) while preserving the relative order of all other fields.
 */
export function sortNameFieldsFirst(configs: FieldConfig[]): FieldConfig[] {
  const nameSet = new Set(NAME_FIELD_ORDER);
  const named = NAME_FIELD_ORDER.map(k =>
    configs.find(c => c.key === k)
  ).filter((c): c is FieldConfig => c !== undefined);
  const rest = configs.filter(c => !nameSet.has(c.key));
  return [...named, ...rest];
}
